<template>
  <div
    :id="id"
    class="element text draggable"
    :class="[
      cssClasses,
      { edit: editing, selected: isSelected, locked },
    ]"
    :style="[basicStyles, transformStyles]"
  >
    <div
      class="text-wrap"
      :class="[
        animationInStyle,
        animationOutStyle,
        'vertical-' + verticalAlign,
      ]"
    >
      <transition
        :enter-active-class="'animated ' + animationIn"
        :leave-active-class="'animated ' + animationOut"
        :duration="{ leave: leaveDuration }"
        mode=""
        @after-enter="afterEnter"
        @before-leave="beforeLeave"
      >
        <div
          v-show="toggle"
          class="text-wrap"
          :class="[loopingClass, { ticker: element?.ticker }]"
          :style="[textShadowStyles, textBackgroundStyles, animationStyles, paddingStyles]"
        >
          <text-editor
            v-if="element"
            ref="editor"
            :editable="!isMapped('text') || labelOverride"
            :editing="editing"
            :element="element"
            :is-selected="isSelected"
            v-model:font-size="fontSize"
            v-model:text="text"
            :data-provider="dataProvider"
          />
        </div>
      </transition>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import ElementsMixins from '../../mixins/elements-mixins';
import MoveMixins from '../../mixins/move-mixins';
import ResizeMixins from '../../mixins/resize-mixins';

import TextEditorComponent from '../TextEditor.vue';

export default {
  name: 'TextElement',

  components: {
    'text-editor': TextEditorComponent,
  },
  mixins: [ElementsMixins, ResizeMixins, MoveMixins],

  data() {
    return {
      editMode: false,
    };
  },

  watch: {
    isSelected(newVal) {
      if (!this.isSelected && !this.group) {
        if (!this.translateDrag[0].enabled()) {
          this.translateDrag[0].enable();
          TweenMax.set(this.$el, this.tweenMaxTransforms);
        }
      }

      if (!newVal) {
        this.editMode = false;
      }
    },
  },

  mounted() {
    this.$el.addEventListener('dblclick', this.edit);
    document.addEventListener('keydown', this.onKeyDown);
  },

  beforeUnmount() {
    this.$el.removeEventListener('dblclick', this.edit);
    document.removeEventListener('keydown', this.onKeyDown);
  },

  computed: {
    ...mapGetters('graphicsTemplate', [
      'getDataValue',
    ]),

    fontSize: {
      get() {
        return this.styles?.fontSize;
      },
      set(v) {
        this.updateStyles({
          id: this.id,
          propKey: 'fontSize',
          propValue: v,
        },
        // blacklist undo/redo because this mutation is not made by the user, autotext rezise
        { blacklist: true });
      },
    },
    noWrapClass() {
      return this.needsNoWrap ? 'editor-no-wrap' : '';
    },

    needsNoWrap() {
      // Allow wrap while editing to fix Firefox bug
      if (this.styles?.textFlowOption === 2) {
        return false;
      }

      return true;
    },

    text: {
      get() {
        if (!this.element) {
          return '';
        }

        const { id, text } = this.element;
        if (this.isMapped('text')) {
          return this.getDataValue(id, 'text');
        }

        return text;
      },
      set(v) {
        if (this.labelOverride) {
          const div = document.createElement('div');// temporary element to strip HTML tags
          div.innerHTML = v;
          const textContent = div.textContent || '';
          this.updateElementProp({
            id: this.id,
            propKey: 'name',
            propValue: textContent,
          });
          this.updateElementProp({
            id: this.id,
            propKey: 'data.labelOverride',
            propValue: true,
          });
        }
        this.updateElementProp({
          id: this.id,
          propKey: 'text',
          propValue: v,
        });
      },
    },

    editing() {
      return this.editMode && this.isSelected;
    },
  },

  methods: {
    onKeyDown(e) {
      if (this.editMode && e.key === 'Escape') {
        this.editMode = false;
      }
    },

    edit(e) {
      if (this.editMode) {
        return;
      }

      if (!this.group || this.isSelected) {
        this.editMode = true;
        this.$store.dispatch('setTextEditing', true);
        this.translateDrag[0].disable();
        e.stopPropagation();
      }
    },
  },
};
</script>
